/**
 * Fixed left sidebar — desktop only (lg+).
 * Contains brand, primary nav, and a footer with language selector,
 * Instellingen link, account block, and (admin-only) admin link.
 */
import { NavLink } from "react-router";
import { Spreekballon } from "../Spreekballon";
import { LanguageSwitcher } from "../LanguageSwitcher";
import { useIsAdmin } from "../../lib/useIsAdmin";
import { useT } from "../../lib/use-t";
import { PRIMARY_NAV_ITEMS, IconSettings } from "./navItems";

type SidebarProps = {
  displayName: string;
};

export function Sidebar({ displayName }: SidebarProps) {
  const isAdmin = useIsAdmin();
  const t = useT();

  const initial = displayName.trim().charAt(0).toUpperCase() || "?";

  return (
    <aside
      className="hidden lg:flex flex-col w-[236px] flex-none bg-[var(--panel)] border-r border-[var(--border)] h-screen sticky top-0 overflow-y-auto"
      aria-label={t("chrome.nav.sidebarAriaLabel")}
    >
      {/* Brand */}
      <div className="flex items-center gap-2.5 px-[10px] py-[22px] pb-[22px]">
        <NavLink to="/vandaag" className="flex items-center gap-2.5">
          <Spreekballon size={28} />
          <span className="font-display font-semibold text-[19px] tracking-tight leading-none">
            <span className="text-[var(--accent)]">A2</span>
            {" "}Easy
          </span>
        </NavLink>
      </div>

      {/* Primary nav */}
      <nav className="flex flex-col gap-[3px] px-2" aria-label={t("chrome.nav.primaryNavAriaLabel")}>
        {PRIMARY_NAV_ITEMS.map(({ key, labelKey, route, Icon }) => (
          <NavLink
            key={key}
            to={route}
            aria-current={undefined /* NavLink sets this automatically */}
            className={({ isActive }) =>
              [
                "flex items-center gap-3 px-3 py-[11px] rounded-[var(--radius)] font-medium text-[14.5px] transition-colors duration-150",
                isActive
                  ? "bg-[var(--accent)] text-white [box-shadow:var(--shadow-blue)]"
                  : "text-[var(--ink-muted)] hover:bg-[var(--panel-soft)] hover:text-[var(--ink)]",
              ].join(" ")
            }
          >
            <span className="w-5 h-5 flex-none flex items-center justify-center">
              <Icon width={19} height={19} />
            </span>
            {t(labelKey)}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="mt-auto flex flex-col gap-[3px] px-2 pt-3.5 border-t border-[var(--border)] mx-2">
        {/* Language selector */}
        <div className="px-1 pb-2">
          <LanguageSwitcher variant="sidebar" />
        </div>

        {/* Instellingen */}
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            [
              "flex items-center gap-3 px-3 py-[11px] rounded-[var(--radius)] font-medium text-[14.5px] transition-colors duration-150",
              isActive
                ? "bg-[var(--accent)] text-white [box-shadow:var(--shadow-blue)]"
                : "text-[var(--ink-muted)] hover:bg-[var(--panel-soft)] hover:text-[var(--ink)]",
            ].join(" ")
          }
        >
          <span className="w-5 h-5 flex-none flex items-center justify-center">
            <IconSettings width={19} height={19} />
          </span>
          {t("chrome.nav.instellingen")}
        </NavLink>

        {/* Admin link — visible only for admins */}
        {isAdmin && (
          <NavLink
            to="/admin"
            className={({ isActive }) =>
              [
                "flex items-center gap-3 px-3 py-[11px] rounded-[var(--radius)] font-medium text-[14.5px] transition-colors duration-150 font-mono-badge text-xs uppercase tracking-wider",
                isActive
                  ? "bg-[var(--accent)] text-white [box-shadow:var(--shadow-blue)]"
                  : "text-[var(--ink-faint)] hover:bg-[var(--panel-soft)] hover:text-[var(--ink)]",
              ].join(" ")
            }
          >
            {t("chrome.nav.admin")}
          </NavLink>
        )}

        {/* Account block */}
        <div className="flex items-center gap-2.5 px-3 py-[9px] rounded-[var(--radius)] mt-1">
          <span
            className="w-[30px] h-[30px] rounded-full bg-[var(--accent)] text-white flex-none grid place-items-center font-mono-badge text-xs font-semibold"
            aria-hidden="true"
          >
            {initial}
          </span>
          <div className="min-w-0 leading-tight">
            <div className="font-semibold text-[13.5px] truncate">{displayName}</div>
            <div className="text-[11.5px] text-[var(--ink-faint)]">
              {t("chrome.nav.accountLevel")}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
